import type { Application } from '@hotwired/stimulus';
import { Controller } from '@hotwired/stimulus';
import Mark from 'mark.js/dist/mark.es6';

/**
 * Visually mark content based on a text input.
 * Either set via data-content-mark-tarket="input" on a HTMLInputElement or
 * data-content-mark-input-value="<value>".
 * Uses mark.js under the hood to mark the elements.
 */
export class ContentMarkerController extends Controller<HTMLElement> {
    static values = {
        input: {
            type: String,
            default: '',
        },
        liveUpdate: {
            type: Boolean,
            default: false,
        },
        exclude: {
            type: Array,
            default: [],
        },
    };

    static register(application: Application) {
        application.register('content-marker', this);
    }

    static targets = ['input', 'marking'];

    /**
     * Directly set the value that should be marked
     */
    declare inputValue: string;

    /**
     * Array oof selectors to exclude from being marked
     */
    declare excludeValue: string[];

    /**
     * Input Value HTML Input Element
     */
    declare readonly inputTarget: HTMLInputElement;

    /**
     * Check existence of an Input Target Element
     */
    declare readonly hasInputTarget: boolean;

    /**
     * If set, only elements inside this element will be marked
     */
    declare readonly markingTarget: HTMLElement;

    /**
     * Check if a marking target was set
     */
    declare readonly hasMarkingTarget: boolean;

    /**
     * Whether to react to value changes without a
     * page refresh and live update the marks.
     */
    declare readonly liveUpdateValue: boolean;

    private _markerjs = null;
    private _markerjsOptions = null;

    get value(): string {
        if (this.hasInputTarget) {
            return this.inputTarget?.value;
        }

        return this.inputValue;
    }

    get markerjs() {
        if (!this._markerjs) {
            this._markerjs = new Mark(this.markingTargetElement);
        }

        return this._markerjs;
    }

    get markerjsOptions() {
        if (!this._markerjsOptions) {
            this._markerjsOptions = {
                acrossElements: true,
                exclude: this.excludeValue.reduce((list, value) => {
                    list.push(`${value} *`);
                    return list;
                }, this.excludeValue),
            };
        }

        return this._markerjsOptions;
    }

    get markingTargetElement(): HTMLElement {
        return this.hasMarkingTarget ? this.markingTarget : this.element;
    }

    connect() {
        this.markContent(this.value);
    }

    /**
     * Mark all parts in the content matching the given value
     * @param value The value to mark in the content
     */
    markContent(value: string) {
        if (value) {
            this.markerjs.unmark().mark(value, this.markerjsOptions);
        } else {
            this.markerjs.unmark();
        }
    }

    inputValueChanged(
        value: ContentMarkerController['inputValue'],
        previousValue: ContentMarkerController['inputValue']
    ) {
        if (value !== previousValue) {
            this.markContent(value);
        }
    }

    inputTargetConnected(element: ContentMarkerController['inputTarget']) {
        if (this.liveUpdateValue) {
            element.setAttribute('data-action', 'content-marker#onInputTargetValueChange');
        }
    }

    onInputTargetValueChange() {
        this.markContent(this.value);
    }
}
